import React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';

import AppLayout from './layouts/AppLayout';
import { AuthProvider } from './contexts/AuthContext';

import Dashboard from './Dashboard';
import Orders from './pages/Orders';
import Projects from './pages/Projects';
import Customers from './pages/Customers';
import Payments from './pages/Payments';
import Outsource from './pages/Outsource';
import Contracts from './pages/Contracts';
import Files from './pages/Files';
import Users from './pages/Users';
import Settings from './pages/Settings';
import ProjectDetail from './pages/ProjectDetail';
import Login from './pages/Login';
import Invoices from './pages/Invoices';
import ExternalChat from './pages/ExternalChat';

function RoleGuard({ allowed, children }) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  if (!allowed.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const [, payloadB64] = token.split('.');
    if (!payloadB64) return true;
    const normalized = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(normalized));
    return !payload.exp || payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

function RequireAuth({ children }) {
  const token = localStorage.getItem('token');
  const location = useLocation();

  if (!token || isTokenExpired(token)) {
    if (token) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    const target = token ? '/login?reason=expired' : '/login';
    return <Navigate to={target} state={{ from: location }} replace />;
  }
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* ログイン不要 */}
          <Route path="/external/chat/:token" element={<ExternalChat />} />
          <Route path="/login" element={<Login />} />

          {/* 認証必須 */}
          <Route
            path="/*"
            element={
              <RequireAuth>
                <AppLayout>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/projects/:id" element={<ProjectDetail />} />

                    <Route
                      path="/orders"
                      element={
                        <RoleGuard allowed={['admin', 'manager', 'developer']}>
                          <Orders />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/customers"
                      element={
                        <RoleGuard allowed={['admin', 'manager', 'developer']}>
                          <Customers />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/payments"
                      element={
                        <RoleGuard allowed={['admin', 'manager', 'developer']}>
                          <Payments />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/invoices"
                      element={
                        <RoleGuard allowed={['admin', 'manager', 'developer']}>
                          <Invoices />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/contracts"
                      element={
                        <RoleGuard allowed={['admin', 'manager', 'developer']}>
                          <Contracts />
                        </RoleGuard>
                      }
                    />
                    <Route path="/files" element={<Files />} />
                    <Route path="/users" element={<Users />} />
                    <Route
                      path="/settings"
                      element={
                        <RoleGuard allowed={['admin']}>
                          <Settings />
                        </RoleGuard>
                      }
                    />
                    <Route
                      path="/outsource"
                      element={
                        <RoleGuard allowed={['admin', 'manager', 'developer']}>
                          <Outsource />
                        </RoleGuard>
                      }
                    />
                  </Routes>
                </AppLayout>
              </RequireAuth>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
